'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuth } from '@/auth/AuthProvider'
import { EditDishPriceModal } from '@/components/pos/EditDishPriceModal'
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { Input } from '@/components/ui/input'
import type { OrderItem } from '@/lib/pos-types'
import { cn, formatPrice, toMs } from '@/lib/utils'
import { MeseroFrame } from '@/modules/mesero/components/MeseroFrame'
import { useMesero } from '@/modules/mesero/MeseroProvider'

export default function MesaItemsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const tableId = String(params.tableId ?? '')
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const { apiEnabled, apiOnline, offlineQueueCount, refreshNow, tables, orders, products, activityLogs, getOrderForTableId, saveOrderDraft, sendOrderToKitchen, updateOrderItems, requestPayment } = useMesero()
  const productsById = useMemo(() => new Map<string, (typeof products)[number]>((products ?? []).map((p) => [p.id, p])), [products])
  const [editingPriceItem, setEditingPriceItem] = useState<OrderItem | null>(null)

  const LOCAL_STORAGE_KEY = useMemo(() => `mfoodx_mesero_draft_notes_${tableId}`, [tableId])
  const [draftNotes, setDraftNotes] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem(`mfoodx_mesero_draft_notes_${tableId}`)
      return saved ? JSON.parse(saved) : {}
    } catch {
      return {}
    }
  })

  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const [noteText, setNoteText] = useState('')

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(draftNotes))
  }, [draftNotes, LOCAL_STORAGE_KEY])

  const saveCurrentNote = (text: string) => {
    if (!editingItemId) return
    const trimmed = text.trim()
    if (trimmed.length > 120) {
      toast.error('La nota no puede superar 120 caracteres')
      return
    }
    const nextDrafts = {
      ...draftNotes,
      [editingItemId]: trimmed || '',
    }
    setDraftNotes(nextDrafts)

    if (table) {
      const itemsToSend = effectiveItems.map((item) => {
        if (item.id === editingItemId) {
          return { ...item, notes: trimmed || undefined }
        }
        const draft = nextDrafts[item.id]
        if (draft !== undefined) {
          return { ...item, notes: draft || undefined }
        }
        return item
      })
      saveOrderDraft(table.id, itemsToSend)
    }
  }

  const saveNote = () => {
    if (!editingItemId) return
    saveCurrentNote(noteText)
    setEditingItemId(null)
    setNoteText('')
  }

  const draftItems = useMemo(() => {
    const state = location.state as unknown
    if (!state || typeof state !== 'object') return null
    const items = (state as { items?: unknown }).items
    if (!Array.isArray(items)) return null
    return items as OrderItem[]
  }, [location.state])

  const table = useMemo(() => tables.find((t) => t.id === tableId) ?? null, [tableId, tables])
  const order = useMemo(() => {
    if (!tableId) return null
    const t = tables.find((x) => x.id === tableId) ?? null
    if (t?.status === 'available' && !t.currentOrderId) return null
    if (t?.currentOrderId) {
      const byId = orders.find((o) => o.id === t.currentOrderId) ?? null
      if (byId && byId.status !== 'closed' && byId.status !== 'paid') return byId
    }
    return getOrderForTableId(tableId)
  }, [getOrderForTableId, orders, tableId, tables])

  useEffect(() => {
    if (!order) {
      setDraftNotes({})
      try {
        localStorage.removeItem(LOCAL_STORAGE_KEY)
      } catch {
        // ignore
      }
    }
  }, [order, LOCAL_STORAGE_KEY])

  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])

  const hadActiveOrderRef = useRef<boolean>(false)

  useEffect(() => {
    if (order && order.status !== 'closed' && order.status !== 'paid') {
      hadActiveOrderRef.current = true
    }
  }, [order])

  useEffect(() => {
    const isTableClosed = (table?.status === 'available' && !table?.currentOrderId) || (order && (order.status === 'closed' || order.status === 'paid'))
    if (isTableClosed && hadActiveOrderRef.current) {
      toast.info('La mesa fue cerrada por un administrador', {
        description: table ? `Mesa ${table.number} ya está libre` : 'Comanda cerrada',
      })
      navigate('/mesero/mesas', { replace: true })
    }
  }, [table, order, navigate])

  useEffect(() => {
    if (!apiEnabled) return
    if (!table) return
    if (order) return
    void refreshNow()
  }, [apiEnabled, order, refreshNow, table])

  const effectiveItems = useMemo(() => {
    if (order) return order.items
    return draftItems ?? []
  }, [draftItems, order])

  const effectiveStatus = useMemo(() => {
    if (order) return order.status
    if (effectiveItems.length > 0) return 'open'
    return null
  }, [effectiveItems.length, order])

  const badge = useMemo(() => {
    if (!effectiveStatus) return null
    if (effectiveStatus === 'in-kitchen') return { text: 'En cocina', variant: 'cocina' as const }
    if (effectiveStatus === 'ready') return { text: 'Listo', variant: 'listo' as const }
    if (effectiveStatus === 'pending-payment') return { text: 'Cobro', variant: 'cobro' as const }
    if (effectiveStatus === 'closed') return { text: 'Cerrada', variant: 'cerrada' as const }
    if (effectiveStatus === 'delivered') return { text: 'Entregado', variant: 'entregado' as const }
    return { text: 'Abierta', variant: 'abierta' as const }
  }, [effectiveStatus])

  const hasAdminAdjustments = useMemo(() => {
    return effectiveItems.some((i) => i.status === 'cancelled')
  }, [effectiveItems])

  const cancelledItems = useMemo(() => {
    return effectiveItems.filter((i) => i.status === 'cancelled')
  }, [effectiveItems])

  const nonCancelledItems = useMemo(() => {
    return effectiveItems.filter((i) => i.status !== 'cancelled')
  }, [effectiveItems])

  const hasRecentAdminAdjust = useMemo(() => {
    if (!table) return false
    const now = Date.now()
    const windowMs = 60 * 60 * 1000
    return (activityLogs ?? []).some((a) => {
      if (a.action !== 'order_adjusted') return false
      if (!a.tableNumber) return false
      if (a.tableNumber !== table.number) return false
      return toMs(a.timestamp) >= now - windowMs
    })
  }, [activityLogs, table])

  const showAdjustBanner = hasAdminAdjustments || hasRecentAdminAdjust

  const toggleSelected = (itemId: string) => {
    setSelectedItemIds((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]))
  }

  const handleRemoveItem = (itemId: string) => {
    const next = effectiveItems.filter((i) => i.id !== itemId)
    if (order) {
      updateOrderItems(order.id, next)
    } else if (table) {
      saveOrderDraft(table.id, next)
    }
    toast.success('Producto eliminado de la comanda')
  }

  const handleUpdateItemQuantity = (itemId: string, delta: number) => {
    const item = effectiveItems.find((i) => i.id === itemId)
    if (!item) return
    const newQty = item.quantity + delta
    if (newQty <= 0) {
      handleRemoveItem(itemId)
      return
    }
    const next = effectiveItems.map((i) => (i.id === itemId ? { ...i, quantity: newQty } : i))
    if (order) {
      updateOrderItems(order.id, next)
    } else if (table) {
      saveOrderDraft(table.id, next)
    }
  }

  const markSelectedDelivered = () => {
    if (!order) return
    if (selectedItemIds.length === 0) return
    const selected = new Set(selectedItemIds)
    const next: OrderItem[] = order.items.map((i) => (selected.has(i.id) ? { ...i, status: 'delivered' as const } : i))
    updateOrderItems(order.id, next)
    setSelectedItemIds([])
    const cancelledCount = cancelledItems.length
    toast.success('Productos entregados', {
      description:
        cancelledCount > 0
          ? `Entregados: ${selectedItemIds.length} · Anulados (Admin): ${cancelledCount}`
          : `Entregados: ${selectedItemIds.length}`,
    })
    if (cancelledCount > 0) {
      const names = cancelledItems.map((i) => i.productName).filter((s) => Boolean(s)).slice(0, 3)
      toast.warning('Productos anulados por Admin', {
        description: names.length > 0 ? names.join(' · ') : `Anulados: ${cancelledCount}`,
      })
    }
    navigate('/mesero/mesas')
  }

  const canRequestPayment = Boolean(order && order.status !== 'closed')
  const readyItemIds = useMemo(() => effectiveItems.filter((i) => i.status === 'ready').map((i) => i.id), [effectiveItems])
  const selectedReadyCount = useMemo(() => selectedItemIds.filter((id) => readyItemIds.includes(id)).length, [readyItemIds, selectedItemIds])
  const allReadySelected = readyItemIds.length > 0 && selectedReadyCount === readyItemIds.length
  const selectAllStickyRef = useRef(false)

  const toggleSelectAllReady = () => {
    if (!order) return
    if (readyItemIds.length === 0) return
    selectAllStickyRef.current = !allReadySelected
    setSelectedItemIds(allReadySelected ? [] : readyItemIds)
  }

  useEffect(() => {
    if (!order) {
      setSelectedItemIds([])
      selectAllStickyRef.current = false
      return
    }
    const readySet = new Set(readyItemIds)
    setSelectedItemIds((prev) => {
      if (selectAllStickyRef.current) return readyItemIds
      return prev.filter((id) => readySet.has(id))
    })
  }, [order, readyItemIds])

  const hasModifiedNotes = useMemo(() => {
    return effectiveItems.some((item) => {
      const draft = draftNotes[item.id]
      return draft !== undefined && draft !== (item.notes ?? '')
    })
  }, [effectiveItems, draftNotes])

  const canSendToKitchen = useMemo(() => {
    if (!table) return false
    if (effectiveItems.length === 0) return false
    const hasPendingItems = effectiveItems.some((i) => i.status === 'pending')
    return hasPendingItems || hasModifiedNotes
  }, [effectiveItems, table, hasModifiedNotes])

  const handleSendToKitchen = () => {
    if (!table) return
    if (effectiveItems.length === 0) {
      toast.error('Agrega al menos un producto')
      return
    }

    const itemsToSend = effectiveItems.map((item) => {
      const draft = draftNotes[item.id]
      if (draft !== undefined) {
        return { ...item, notes: draft || undefined }
      }
      return item
    })

    sendOrderToKitchen(table.id, itemsToSend)

    // Clear draft notes
    localStorage.removeItem(LOCAL_STORAGE_KEY)
    setDraftNotes({})

    if (apiEnabled && apiOnline) {
      toast.success('Comanda enviada a cocina', { description: `Mesa ${table.number} (${table.zone}) · ${effectiveItems.length} items` })
    } else {
      toast.warning('Sin conexión', { description: `Comanda guardada y pendiente de sincronización · Mesa ${table.number} (${table.zone})` })
    }
    navigate('/mesero/mesas')
  }

  const handleRequestPayment = () => {
    if (!order || !table) return
    requestPayment(order.id)
    if (apiEnabled && apiOnline) {
      toast.success(order.status === 'pending-payment' ? 'Cobro reenviado' : 'Cobro solicitado', { description: `Mesa ${table.number} (${table.zone})` })
      navigate('/mesero/mesas')
      return
    }
    toast.warning('Sin conexión', {
      description: `Cobro pendiente de sincronización · Cola: ${offlineQueueCount}`,
    })
    navigate('/mesero/mesas')
  }

  if (!table) {
    return (
      <MeseroFrame>
        <div className="flex-1 flex flex-col items-center justify-center text-[#9a9aa8]">Mesa no encontrada</div>
      </MeseroFrame>
    )
  }

  return (
    <MeseroFrame>
      <div className="flex-1 overflow-hidden flex flex-col bg-[#0f0f11]">
        <div className="px-5 pt-3 pb-2 flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => navigate('/mesero/mesas')}
            className="w-9 h-9 rounded-full bg-[#1e1e23] border border-white/10 flex items-center justify-center"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="#f0f0f0" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="flex-1">
            <div className="text-[18px] font-semibold text-[#f0f0f0] leading-tight flex items-baseline gap-1.5">
              <span>Mesa {table.number}</span>
              <span className="text-[12px] font-normal text-[#9a9aa8]">({table.zone})</span>
            </div>
          </div>
          {badge && (
            <div
              className={cn(
                'px-2.5 py-1 rounded-full text-[11px] font-medium border',
                badge.variant === 'cocina' && 'bg-[rgba(245,166,35,0.12)] text-[#f5a623] border-[rgba(245,166,35,0.35)]',
                badge.variant === 'listo' && 'bg-[rgba(0,229,160,0.12)] text-[#00e5a0] border-[rgba(0,229,160,0.25)]',
                badge.variant === 'cobro' && 'bg-[rgba(0,229,160,0.12)] text-[#00e5a0] border-[rgba(0,229,160,0.25)]',
                badge.variant === 'cerrada' && 'bg-[#26262d] text-[#5a5a6a] border-white/10',
                badge.variant === 'entregado' && 'bg-[#26262d] text-[#9a9aa8] border-white/10',
                badge.variant === 'abierta' && 'bg-[#26262d] text-[#9a9aa8] border-white/10',
              )}
            >
              {badge.text}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5">
          {effectiveItems.length === 0 ? (
            <div className="py-10 text-center text-[#9a9aa8]">
              <div className="text-sm font-medium text-[#f0f0f0]">Sin items</div>
              <div className="text-xs mt-1">Agrega productos a la comanda</div>
              <button
                type="button"
                onClick={() => navigate(`/mesero/comanda/${table.id}`)}
                className="mt-4 px-4 py-3 rounded-xl bg-[#00e5a0] text-black text-sm font-semibold"
              >
                Tomar pedido
              </button>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {showAdjustBanner && (
                <div className="py-3">
                  <div className="rounded-xl border border-[rgba(217,70,239,0.35)] bg-[rgba(217,70,239,0.10)] px-3 py-2">
                    <div className="text-[12px] font-semibold text-[#f0f0f0]">Ajuste de comanda (Admin)</div>
                    <div className="text-[11px] text-[#d946ef] mt-0.5">
                      {cancelledItems.length > 0
                        ? 'Hay productos anulados y/o agregados nuevamente'
                        : 'Se ajustaron cantidades y/o notas de la comanda'}
                    </div>
                  </div>
                </div>
              )}
              {cancelledItems.length > 0 && (
                <div className="py-3">
                  <div className="rounded-xl border border-[rgba(239,68,68,0.35)] bg-[rgba(239,68,68,0.10)] px-3 py-2">
                    <div className="text-[12px] font-semibold text-[#f0f0f0]">Anulados por Admin</div>
                    <div className="text-[11px] text-[#ef4444] mt-0.5">Estos productos se deben dejar de preparar/entregar</div>
                  </div>
                  <div className="mt-2 space-y-2">
                    {cancelledItems.map((item) => (
                      <div key={item.id} className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full shrink-0 bg-[#ef4444]" />
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-medium text-[#ef4444] truncate line-through">
                            ANULAR: {item.productName}
                          </div>
                          {item.notes && <div className="text-[11px] text-[#9a9aa8] mt-0.5 truncate">{item.notes}</div>}
                        </div>
                        <div className="font-mono text-[12px] text-[#9a9aa8]">{item.quantity}x</div>
                        <div className="px-2 py-1 rounded-full text-[10px] font-medium shrink-0 bg-[rgba(239,68,68,0.12)] text-[#ef4444]">
                          Anulado
                        </div>
                        <div className="w-7 h-7 rounded-full border border-white/10 opacity-40 shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {nonCancelledItems
                .slice()
                .sort((a, b) => {
                  const aCancelled = a.status === 'cancelled'
                  const bCancelled = b.status === 'cancelled'
                  if (aCancelled !== bCancelled) return aCancelled ? -1 : 1
                  const aReady = a.status === 'ready'
                  const bReady = b.status === 'ready'
                  if (aReady !== bReady) return aReady ? -1 : 1
                  const aDelivered = a.status === 'delivered'
                  const bDelivered = b.status === 'delivered'
                  if (aDelivered !== bDelivered) return aDelivered ? 1 : -1
                  return toMs(a.addedAt) - toMs(b.addedAt)
                })
                .map((item) => {
                const status = item.status
                const isDelivered = status === 'delivered'
                const canDeliver = status === 'ready'
                const isSelected = selectedItemIds.includes(item.id)
                return (
                  <div key={item.id} className="py-3.5 border-b border-white/5 flex gap-3">
                    <div
                      className={cn(
                        'w-2 h-2 rounded-full shrink-0 mt-1.5',
                        status === 'pending' && 'bg-[#5a5a6a]',
                        (status === 'sent' || status === 'in-progress') && 'bg-[#f5a623]',
                        status === 'ready' && 'bg-[#00e5a0]',
                        status === 'delivered' && 'bg-[#5a5a6a] opacity-40',
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        {isDelivered ? (
                          <div className="text-[15px] font-medium text-[#5a5a6a] line-through flex-1 leading-snug">
                            {item.productName}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingItemId(item.id)
                              setNoteText(draftNotes[item.id] !== undefined ? draftNotes[item.id] : (item.notes ?? ''))
                            }}
                            className="text-left flex-1 focus:outline-none group"
                          >
                            <div className="text-[15px] font-medium text-[#f0f0f0] group-hover:text-[#00e5a0] transition-colors leading-snug">
                              {item.productName}
                            </div>
                          </button>
                        )}

                        <div className="flex items-center shrink-0 pt-0.5">
                          {isAdmin ? (
                            <button
                              type="button"
                              onClick={() => setEditingPriceItem(item)}
                              className="px-2 py-0.5 rounded bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-[#f5a623] text-[11px] font-mono font-medium flex items-center gap-1 transition-colors"
                              title="Modificar precio de plato (Admin)"
                            >
                              <span>{formatPrice(item.unitPrice * item.quantity)}</span>
                              <span className="text-[9px]">✏️</span>
                            </button>
                          ) : (
                            <span className="font-mono text-[14px] font-semibold text-[#f0f0f0]">
                              {formatPrice(item.unitPrice * item.quantity)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Nota del plato */}
                      {(() => {
                        const displayedNote = draftNotes[item.id] !== undefined ? draftNotes[item.id] : (item.notes ?? '')
                        const isNoteModified = draftNotes[item.id] !== undefined && draftNotes[item.id] !== (item.notes ?? '')
                        if (displayedNote) {
                          return (
                            <div
                              className={cn(
                                "text-[12px] mt-1 transition-colors",
                                isNoteModified ? "text-[#f5a623] font-medium" : "text-[#9a9aa8]"
                              )}
                            >
                              {isNoteModified ? `📝 ${displayedNote} (Pendiente)` : `📝 ${displayedNote}`}
                            </div>
                          )
                        }
                        return null
                      })()}

                      {/* Botones de acción y controles debajo del nombre del plato */}
                      <div className="flex items-center justify-between gap-1 mt-2 pt-1.5 border-t border-white/5 min-w-0">
                        {!isDelivered ? (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingItemId(item.id)
                              setNoteText(draftNotes[item.id] !== undefined ? draftNotes[item.id] : (item.notes ?? ''))
                            }}
                            className="text-[10px] text-[#9a9aa8] hover:text-[#00e5a0] transition-colors py-0.5 px-1.5 rounded bg-[#1e1e23] border border-white/10 shrink-0 truncate max-w-[90px]"
                          >
                            {draftNotes[item.id] || item.notes ? '📝 Nota' : '📝 + Nota'}
                          </button>
                        ) : (
                          <div />
                        )}

                        <div className="flex items-center gap-1 shrink-0">
                          <div
                            className={cn(
                              'px-1.5 py-0.5 rounded-full text-[9px] font-medium shrink-0 border',
                              status === 'pending' && 'bg-[rgba(156,163,175,0.08)] text-[#9ca3af] border-[rgba(156,163,175,0.20)]',
                              (status === 'sent' || status === 'in-progress') && 'bg-[rgba(245,166,35,0.08)] text-[#f5a623] border-[rgba(245,166,35,0.20)]',
                              status === 'ready' && 'bg-[rgba(0,229,160,0.08)] text-[#00e5a0] border-[rgba(0,229,160,0.20)]',
                              status === 'delivered' && 'bg-[rgba(156,163,175,0.03)] text-[#5a5a6a] border-white/5 opacity-70',
                            )}
                          >
                            {status === 'pending' && 'Pendiente'}
                            {(status === 'sent' || status === 'in-progress') && 'Cocina'}
                            {status === 'ready' && 'Listo'}
                            {status === 'delivered' && 'Entregado'}
                          </div>

                          {status === 'pending' ? (
                            <div className="flex items-center gap-1 shrink-0">
                              <div className="flex items-center gap-1 bg-[#1e1e23] border border-white/10 rounded-md px-1 py-0.5">
                                <button
                                  type="button"
                                  onClick={() => handleUpdateItemQuantity(item.id, -1)}
                                  className="w-5 h-5 rounded flex items-center justify-center text-[13px] font-bold text-[#f0f0f0] bg-[#26262d] border border-white/15 hover:bg-white/10 transition-colors"
                                  title="Disminuir cantidad"
                                >
                                  −
                                </button>
                                <span className="font-mono text-[12px] font-semibold text-[#f0f0f0] min-w-3 text-center">
                                  {item.quantity}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleUpdateItemQuantity(item.id, 1)}
                                  className="w-5 h-5 rounded flex items-center justify-center text-[13px] font-bold text-[#00e5a0] bg-[rgba(0,229,160,0.15)] border border-[#00e5a0] hover:bg-[rgba(0,229,160,0.25)] transition-colors"
                                  title="Aumentar cantidad"
                                >
                                  +
                                </button>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveItem(item.id)}
                                className="w-5 h-5 rounded border border-red-500/30 bg-red-500/10 text-red-400 text-[10px] flex items-center justify-center shrink-0 hover:bg-red-500/20 transition-colors font-bold"
                                title="Eliminar producto de la comanda"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div className="font-mono text-[11px] text-[#9a9aa8] ml-0.5">{item.quantity}x</div>
                          )}

                          <button
                            type="button"
                            onClick={() => canDeliver && toggleSelected(item.id)}
                            disabled={!canDeliver && !isDelivered}
                            className={cn(
                              'w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-colors',
                              isDelivered
                                ? 'bg-[#00e5a0] border-[#00e5a0]'
                                : isSelected
                                  ? 'bg-[#00e5a0] border-[#00e5a0]'
                                  : 'bg-transparent border-[#00e5a0]',
                              (!canDeliver && !isDelivered) && 'border-white/10 opacity-60',
                            )}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              className={cn(
                                'w-3.5 h-3.5',
                                isDelivered
                                  ? 'stroke-black'
                                  : isSelected
                                    ? 'stroke-black'
                                    : canDeliver
                                      ? 'stroke-[#00e5a0]'
                                      : 'stroke-[#5a5a6a]',
                              )}
                              fill="none"
                              strokeWidth="2"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="px-5 pb-6 pt-4 flex flex-col gap-2 shrink-0">
          {canSendToKitchen && (
            <button
              type="button"
              onClick={handleSendToKitchen}
              className="w-full py-4 rounded-2xl bg-[#00e5a0] text-black text-[13px] font-semibold"
            >
              Enviar a cocina
            </button>
          )}
          {readyItemIds.length > 0 && (
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={toggleSelectAllReady}
                className="px-3 py-2 rounded-xl border border-white/10 bg-[#1e1e23] text-[#f0f0f0] text-[12px] font-semibold"
              >
                {allReadySelected ? 'Quitar selección' : `Seleccionar todo (${readyItemIds.length})`}
              </button>
              <div className="text-[11px] text-[#9a9aa8] font-mono">
                {selectedReadyCount}/{readyItemIds.length}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={markSelectedDelivered}
            disabled={!order || selectedReadyCount === 0}
            className={cn(
              'w-full py-4 rounded-2xl text-[13px] font-semibold',
              selectedReadyCount > 0 ? 'bg-[#00e5a0] text-black' : 'bg-[#26262d] text-[#5a5a6a]',
            )}
          >
            {selectedReadyCount > 0 ? `Marcar entregados (${selectedReadyCount})` : 'Marcar entregados'}
          </button>
          <div className="flex gap-2">
              <button
              type="button"
              onClick={handleRequestPayment}
              disabled={!canRequestPayment}
              className="flex-1 py-4 rounded-2xl border border-white/10 bg-[#1e1e23] text-[#f0f0f0] text-[13px] font-medium"
            >
              {order?.status === 'pending-payment' ? 'Reenviar cobro' : 'Solicitar cobro'}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/mesero/comanda/${table.id}`)}
              className="flex-1 py-4 rounded-2xl bg-[#00e5a0] text-black text-[13px] font-semibold disabled:bg-[#26262d] disabled:text-[#5a5a6a]"
              
            >
              + Agregar
            </button>
          
          </div>
        </div>
      </div>

      <Drawer
        open={Boolean(editingItemId)}
        onOpenChange={(open) => {
          if (!open) {
            if (editingItemId) {
              saveCurrentNote(noteText)
            }
            setEditingItemId(null)
            setNoteText('')
          }
        }}
      >
        <DrawerContent className="bg-[#1e1e23] border-t border-white/10 text-[#f0f0f0]">
          <div className="mx-auto mt-4 h-1 w-10 rounded-full bg-white/15" />
          <DrawerHeader className="text-left px-5">
            <DrawerTitle className="text-[#f0f0f0] text-base font-semibold">Editar nota</DrawerTitle>
            <DrawerDescription className="text-[#9a9aa8] text-xs">Instrucciones especiales para este producto</DrawerDescription>
          </DrawerHeader>
          <div className="px-5 pb-2">
            <Input
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onBlur={() => {
                saveCurrentNote(noteText)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur()
                }
              }}
              placeholder="Ej: sin cebolla, término medio..."
              className="bg-[#26262d] border-white/10 text-[#f0f0f0] placeholder:text-[#5a5a6a]"
              maxLength={120}
            />
          </div>
          <DrawerFooter className="px-5 pb-8">
            <div className="flex gap-2">
              <DrawerClose asChild>
                <button
                  type="button"
                  onClick={saveNote}
                  className="flex-1 py-3 rounded-xl bg-[#00e5a0] text-black text-sm font-semibold"
                >
                  Listo
                </button>
              </DrawerClose>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {editingPriceItem && (
        <EditDishPriceModal
          isOpen={Boolean(editingPriceItem)}
          onClose={() => setEditingPriceItem(null)}
          onSave={(newUnitPrice) => {
            if (!order) return
            const next = effectiveItems.map((i) =>
              i.id === editingPriceItem.id ? { ...i, unitPrice: newUnitPrice } : i
            )
            updateOrderItems(order.id, next)
            toast.success(`Precio de ${editingPriceItem.productName} actualizado a ${formatPrice(newUnitPrice)}`)
            setEditingPriceItem(null)
          }}
          productName={editingPriceItem.productName}
          currentUnitPrice={editingPriceItem.unitPrice}
          catalogBasePrice={productsById.get(editingPriceItem.productId)?.price ?? editingPriceItem.unitPrice}
        />
      )}
    </MeseroFrame>
  )
}
